import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlarmClock, ArrowLeft, History, PlayCircle, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { StatusDot } from "@/components/StatusDot";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs } from "@/components/ui/tabs";
import { useSession } from "@/hooks/useSession";
import { api, type Application, type AppSession, type AuthPolicy, type CachePolicy, type CertAppStatus, type Exception_, type LbMember, type LbPool, type MaintenanceState, type OasStatus, type RateList, type Revision } from "@/lib/api";
import { fmtAgo, fmtTime } from "@/lib/utils";

interface Preview {
  live_state: string;
  diff: {
    changed: Record<string, { live: unknown; desired: string }>;
    missing: Record<string, string>;
    unmanaged: Record<string, unknown>;
  };
}

const REV_VARIANT: Record<string, "success" | "warning" | "secondary" | "destructive" | "default"> = {
  applied: "success", converged: "success", applying: "warning", draft: "secondary",
  failed: "destructive", rolled_back: "warning", verifying: "warning", active: "success",
};

export default function ApplicationDetail() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useSession();
  const canOperate = user?.role === "owner" || user?.role === "operator";
  const [tab, setTab] = useState("config");
  const [delOpen, setDelOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [totp, setTotp] = useState("");
  const [delErr, setDelErr] = useState("");

  const { data: app } = useQuery({
    queryKey: ["app", id],
    queryFn: () => api.get<Application>(`/api/applications/${id}`),
    refetchInterval: 10_000,
  });
  const { data: preview } = useQuery({
    queryKey: ["preview", id],
    queryFn: () => api.get<Preview>(`/api/applications/${id}/preview`),
    refetchInterval: 15_000,
  });
  const { data: revisions } = useQuery({
    queryKey: ["revisions", id],
    queryFn: () => api.get<Revision[]>(`/api/applications/${id}/revisions`),
    refetchInterval: 8_000,
  });
  const { data: exceptions } = useQuery({
    queryKey: ["exceptions"],
    queryFn: () => api.get<Exception_[]>("/api/exceptions"),
    refetchInterval: 15_000,
  });

  const apply = useMutation({
    mutationFn: () => api.post(`/api/applications/${id}/apply`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["revisions", id] }),
  });
  const rollback = useMutation({
    mutationFn: (revId: number) =>
      api.post(`/api/applications/${id}/rollback`, { revision_id: revId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["revisions", id] }),
  });
  const del = useMutation({
    mutationFn: async () => {
      await api.post("/api/auth/reauth", { password: pw, ...(totp ? { totp_code: totp } : {}) });
      return api.del(`/api/applications/${id}`);
    },
    onSuccess: () => nav("/applications"),
    onError: (e) => setDelErr(e.message),
  });

  if (!app) {
    return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-40" /></div>;
  }
  const dirty = preview && (Object.keys(preview.diff?.changed ?? {}).length
    + Object.keys(preview.diff?.missing ?? {}).length) > 0;
  const appExc = (exceptions ?? []).filter((e) => e.app_id === id);

  return (
    <div className="space-y-5">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Link to="/applications"><ArrowLeft className="h-5 w-5 text-muted-foreground hover:text-foreground" /></Link>
            {app.name}
            <Badge variant={app.detect_only ? "warning" : "success"}>
              {app.detect_only ? "detection-only" : "blocking"}
            </Badge>
            <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
              <StatusDot status={app.status} />{app.status}
            </span>
          </span>
        }
        description={`${app.server_names.join(", ")} → ${app.origin_scheme}://${app.origin_host}:${app.origin_port}`}
      >
        {canOperate && (
          <>
            <Button size="sm" variant="outline" onClick={() => setDelOpen(true)}>
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
            <Button size="sm" onClick={() => apply.mutate()} disabled={apply.isPending}>
              <PlayCircle className="h-4 w-4" />
              {apply.isPending ? "Queuing…" : "Apply to edge"}
            </Button>
          </>
        )}
      </PageHeader>

      {app.coverage_note && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="px-5 py-3.5 text-sm text-amber-200">
            <b>Coverage:</b> {app.coverage_note}
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onChange={setTab} tabs={[
        { value: "config", label: "Configuration" },
        { value: "exceptions", label: "Exceptions", count: appExc.filter((e) => e.status === "active").length },
        { value: "revisions", label: "Revisions", count: (revisions ?? []).length },
        { value: "premium", label: "Premium" },
      ]} />

      {tab === "config" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Service</CardTitle>
              <CardDescription>BunkerWeb service <code className="font-mono">{app.bw_service}</code></CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              {[
                ["server names", app.server_names.join(" ")],
                ["origin", `${app.origin_scheme}://${app.origin_host}:${app.origin_port}`],
                ["profile", app.profile],
                ["cert", app.cert_method],
                ["status", app.status],
                ["created", fmtAgo(app.created_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="truncate font-mono text-xs" title={v}>{v}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Drift / pending diff</CardTitle>
              <CardDescription>
                {preview ? (dirty ? "desired state differs from live edge" : "in sync with live edge") : "checking…"}
              </CardDescription>
            </CardHeader>
            <CardContent className="max-h-56 overflow-auto font-mono text-xs">
              {preview && !dirty && <span className="text-emerald-300">✓ no diff — live matches desired</span>}
              {Object.entries(preview?.diff?.missing ?? {}).map(([k, v]) => (
                <div key={k} className="text-sky-300">+ {k} = {v}</div>
              ))}
              {Object.entries(preview?.diff?.changed ?? {}).map(([k, d]) => (
                <div key={k} className="text-amber-300">~ {k}: {String(d.live)} → {d.desired}</div>
              ))}
              {Object.keys(preview?.diff?.unmanaged ?? {}).length > 0 && (
                <div className="mt-2 text-muted-foreground">
                  unmanaged on edge: {Object.keys(preview!.diff!.unmanaged).join(", ")}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "exceptions" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">WAF exceptions</CardTitle>
            <CardDescription>
              Materialized into this service's modsec-crs config; they always
              expire — the worker de-materializes automatically.
            </CardDescription>
          </CardHeader>
          <Table>
            <THead>
              <TR><TH>Status</TH><TH>Rule</TH><TH>Scope</TH><TH>Expires</TH><TH>Reason</TH></TR>
            </THead>
            <TBody>
              {appExc.map((e) => (
                <TR key={e.id}>
                  <TD><Badge variant={e.status === "active" ? "success" : "secondary"}>{e.status}</Badge></TD>
                  <TD className="font-mono text-xs">{e.rule_id}</TD>
                  <TD className="font-mono text-xs">
                    {e.scope.path ? `path ${e.scope.path}` : e.scope.arg ? `arg ${e.scope.arg}` : "global"}
                  </TD>
                  <TD className="text-xs">
                    <span className="flex items-center gap-1">
                      <AlarmClock className="h-3 w-3" />{fmtTime(e.expires_at)}
                    </span>
                  </TD>
                  <TD className="max-w-[240px] truncate text-xs text-muted-foreground" title={e.reason}>{e.reason}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
          {appExc.length === 0 && (
            <EmptyState icon={AlarmClock} title="No exceptions on this app"
              hint="Create one from a blocked event on the Security Events page." />
          )}
        </Card>
      )}

      {tab === "premium" && <PremiumTab appId={id} appName={app.name} canOperate={canOperate} />}

      {tab === "revisions" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4" /> Revisions
            </CardTitle>
            <CardDescription>
              Apply is asynchronous — the scheduler regenerates, pushes and
              reloads the edge (~45–90s observed). Rollback re-applies that
              revision's desired state.
            </CardDescription>
          </CardHeader>
          <Table>
            <THead>
              <TR><TH>#</TH><TH>Status</TH><TH>By</TH><TH>Created</TH><TH>Applied</TH><TH></TH></TR>
            </THead>
            <TBody>
              {(revisions ?? []).map((r) => (
                <TR key={r.id}>
                  <TD className="font-mono text-xs">r{r.seq}{r.is_rollback && " ↩"}</TD>
                  <TD>
                    <Badge variant={REV_VARIANT[r.status] ?? "secondary"}>{r.status}</Badge>
                    {r.error && <span className="ml-2 text-xs text-destructive">{r.error}</span>}
                  </TD>
                  <TD className="text-xs">{r.created_by}</TD>
                  <TD className="text-xs">{fmtTime(r.created_at)}</TD>
                  <TD className="text-xs">{fmtTime(r.applied_at)}</TD>
                  <TD className="text-right">
                    {canOperate && (r.status === "applied" || r.status === "active") && (
                      <Button size="sm" variant="outline"
                        onClick={() => rollback.mutate(r.id)} disabled={rollback.isPending}>
                        <RotateCcw className="h-3 w-3" /> Roll back here
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          {(revisions ?? []).length === 0 && (
            <EmptyState icon={History} title="Nothing applied yet"
              hint="Use 'Apply to edge' to create revision 1." />
          )}
        </Card>
      )}

      <Dialog open={delOpen} onClose={() => setDelOpen(false)}
        title={`Delete ${app.name}?`}
        description="Removes the BunkerWeb service, its custom configs and this record. Requires password re-authentication — the session must have verified recently.">
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setDelErr(""); del.mutate(); }}>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus />
          </div>
          {user?.totp_enabled && (
            <div className="space-y-1.5">
              <Label>TOTP code</Label>
              <Input value={totp} onChange={(e) => setTotp(e.target.value)} placeholder="123456" />
            </div>
          )}
          {delErr && <p className="text-sm text-destructive">{delErr}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={del.isPending}>
              {del.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Premium tab — nx.maintenance / nx.pages / nx.certs surfaces for this app.
// Module-disabled states are shown honestly (the API returns 409).
// ---------------------------------------------------------------------------
function PremiumTab({ appId, appName, canOperate }: {
  appId: string; appName: string; canOperate: boolean;
}) {
  const qc = useQueryClient();
  const [until, setUntil] = useState("");
  const [cidrs, setCidrs] = useState("");
  const [paths, setPaths] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");

  const maint = useQuery({
    queryKey: ["maintenance", appId],
    queryFn: () => api.get<{ apps: MaintenanceState[] }>("/api/maintenance/apps")
      .then((d) => d.apps.find((a) => a.app_id === appId) ?? null)
      .catch((e: Error) => ({ moduleOff: e.message } as const)),
    refetchInterval: 10_000,
  });
  const certs = useQuery({
    queryKey: ["cert-status", appId],
    queryFn: () => api.get<{ apps: CertAppStatus[] }>("/api/certs/status")
      .then((d) => d.apps.find((a) => a.app_id === appId) ?? null)
      .catch(() => null),
    refetchInterval: 60_000,
  });
  const maintMut = useMutation({
    mutationFn: (enabled: boolean) => api.put(`/api/maintenance/apps/${appId}`, {
      enabled,
      until: until ? new Date(until).toISOString() : null,
      allow_paths: paths.split(",").map((s) => s.trim()).filter(Boolean),
      allow_cidrs: cidrs.split(",").map((s) => s.trim()).filter(Boolean),
      reason,
    }),
    onSuccess: () => { setErr(""); qc.invalidateQueries({ queryKey: ["maintenance", appId] }); },
    onError: (e) => setErr(e.message),
  });
  const bindMut = useMutation({
    mutationFn: (bound: boolean) => api.post(`/api/pages/apps/${appId}/bind`, { bound }),
    onSuccess: () => setErr(""),
    onError: (e) => setErr(e.message),
  });

  const m = maint.data;
  const moduleOff = m && "moduleOff" in m;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Maintenance mode</CardTitle>
          <CardDescription>
            nx.maintenance — edge answers 503 + retry-after during the window.
            ACME challenges always pass; allowlisted paths/addresses keep
            recovery access. Expiry is worker-enforced.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {moduleOff && <p className="text-muted-foreground">Module disabled — enable nx.maintenance on the Modules page.</p>}
          {m && !("moduleOff" in m) && (
            <div className="flex items-center gap-2">
              <Badge variant={m.enabled ? "warning" : "secondary"}>
                {m.enabled ? "in maintenance" : "serving"}
              </Badge>
              {m.until && <span className="text-xs text-muted-foreground">until {fmtTime(m.until)}</span>}
            </div>
          )}
          {canOperate && !moduleOff && (
            <div className="space-y-2">
              <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
              <Input placeholder="recovery CIDRs (comma-separated, e.g. 10.0.0.0/8)"
                value={cidrs} onChange={(e) => setCidrs(e.target.value)} />
              <Input placeholder="allowed paths (comma-separated, e.g. /healthz)"
                value={paths} onChange={(e) => setPaths(e.target.value)} />
              <Input placeholder="reason (shown in audit)" value={reason}
                onChange={(e) => setReason(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => maintMut.mutate(true)} disabled={maintMut.isPending}>
                  Enter maintenance
                </Button>
                <Button size="sm" variant="outline" onClick={() => maintMut.mutate(false)}>
                  Exit maintenance
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Custom pages</CardTitle>
          <CardDescription>
            nx.pages — branded block/maintenance pages for {appName}'s
            edge-generated 403/503s. Origin errors pass through untouched.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {canOperate && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => bindMut.mutate(true)} disabled={bindMut.isPending}>
                Bind branded pages
              </Button>
              <Button size="sm" variant="outline" onClick={() => bindMut.mutate(false)}>
                Unbind
              </Button>
              <Link to="/pages" className="ml-auto">
                <Button size="sm" variant="ghost">manage templates</Button>
              </Link>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Templates render server-side with escaped variables; app-scoped
            versions override the global template.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Certificate</CardTitle>
          <CardDescription>nx.certs — method, expiry probe, renewal state.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5 text-sm">
          {!certs.data && <p className="text-muted-foreground">nx.certs disabled or no check yet.</p>}
          {certs.data && (
            <>
              <div className="flex justify-between"><span className="text-muted-foreground">method</span><span className="font-mono text-xs">{certs.data.cert_method}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">wildcards</span><span className="font-mono text-xs">{certs.data.wildcards.join(" ") || "—"}</span></div>
              {certs.data.state && (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">days left</span><span className="font-mono text-xs">{certs.data.state.days_left ?? "—"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">renewal</span><span className="font-mono text-xs">{certs.data.state.renewal_status}</span></div>
                  {certs.data.state.error && <p className="text-xs text-destructive">{certs.data.state.error}</p>}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <LbCard appId={appId} canOperate={canOperate} />
      <CacheCard appId={appId} canOperate={canOperate} />
      <OasCard appId={appId} canOperate={canOperate} />
      <AuthCard appId={appId} canOperate={canOperate} />
      <RateCard appId={appId} canOperate={canOperate} />
      {err && <p className="lg:col-span-2 text-sm text-destructive">{err}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// M3 — nx.loadbalance / nx.cache / nx.openapi cards
// ---------------------------------------------------------------------------
function LbCard({ appId, canOperate }: { appId: string; canOperate: boolean }) {
  const qc = useQueryClient();
  const [err, setErr] = useState("");
  const [algo, setAlgo] = useState("round_robin");
  const [healthPath, setHealthPath] = useState("/get");
  const [members, setMembers] = useState("");

  const lb = useQuery({
    queryKey: ["lb", appId],
    queryFn: () => api.get<{ pool: LbPool | null; members: LbMember[] }>(
      `/api/lb/apps/${appId}`).catch(() => null),
    refetchInterval: 10_000,
  });
  const saveMut = useMutation({
    mutationFn: (enabled: boolean) => api.put(`/api/lb/apps/${appId}`, {
      enabled, algorithm: algo, health_path: healthPath,
    }),
    onSuccess: () => { setErr(""); qc.invalidateQueries({ queryKey: ["lb", appId] }); },
    onError: (e) => setErr(e.message),
  });
  const membersMut = useMutation({
    mutationFn: () => api.put(`/api/lb/apps/${appId}/members`,
      members.split(",").map((s) => {
        const [host, port] = s.trim().split(":");
        return { host, port: Number(port || 8080) };
      }).filter((m) => m.host)),
    onSuccess: () => { setErr(""); qc.invalidateQueries({ queryKey: ["lb", appId] }); },
    onError: (e) => setErr(e.message),
  });
  const memberAct = useMutation({
    mutationFn: ({ id, op }: { id: string; op: string }) =>
      api.post(`/api/lb/apps/${appId}/members/${id}/${op}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lb", appId] }),
    onError: (e) => setErr(e.message),
  });

  const pool = lb.data?.pool;
  const ms = lb.data?.members ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Load balancing</CardTitle>
        <CardDescription>
          nx.loadbalance — origin pool over an OSS nginx upstream. Members
          are health-probed; down members drop out and recover by
          consecutive-check thresholds. POST/PUT are never replayed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {lb.data === null && <p className="text-muted-foreground">Module disabled — enable nx.loadbalance.</p>}
        {pool && (
          <div className="flex items-center gap-2">
            <Badge variant={pool.enabled ? "success" : "secondary"}>
              {pool.enabled ? pool.algorithm : "off"}
            </Badge>
            <span className="text-xs text-muted-foreground">
              health {pool.health_path} · down after {pool.down_after} ·
              recover after {pool.recover_after}
            </span>
          </div>
        )}
        {ms.length > 0 && (
          <Table>
            <THead><TR><TH>member</TH><TH>w</TH><TH>state</TH><TH /></TR></THead>
            <TBody>
              {ms.map((m) => (
                <TR key={m.id}>
                  <TD className="font-mono text-xs">{m.host}:{m.port}</TD>
                  <TD className="text-xs">{m.weight}</TD>
                  <TD>
                    <Badge variant={
                      m.state === "up" ? "success" :
                      m.draining ? "warning" :
                      m.state === "down" ? "destructive" : "secondary"}>
                      {m.draining ? "draining" : m.state}
                    </Badge>
                    {m.last_error && <span className="ml-1 text-[10px] text-muted-foreground">{m.last_error}</span>}
                  </TD>
                  <TD className="text-right">
                    {canOperate && !m.draining && m.enabled && (
                      <Button size="sm" variant="ghost"
                        onClick={() => memberAct.mutate({ id: m.id, op: "drain" })}>
                        drain
                      </Button>
                    )}
                    {canOperate && m.draining && (
                      <Button size="sm" variant="ghost"
                        onClick={() => memberAct.mutate({ id: m.id, op: "restore" })}>
                        restore
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {canOperate && lb.data !== null && (
          <div className="space-y-2 border-t pt-2">
            <div className="flex gap-2">
              <select className="rounded border bg-background px-2 py-1 text-sm"
                value={algo} onChange={(e) => setAlgo(e.target.value)}>
                <option value="round_robin">round_robin</option>
                <option value="least_conn">least_conn</option>
                <option value="ip_hash">ip_hash</option>
                <option value="hash_uri">hash uri</option>
              </select>
              <Input placeholder="health path" value={healthPath}
                onChange={(e) => setHealthPath(e.target.value)} />
            </div>
            <Input placeholder="members host:port, comma-separated"
              value={members} onChange={(e) => setMembers(e.target.value)} />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => membersMut.mutate()}
                disabled={membersMut.isPending}>set members</Button>
              <Button size="sm" variant="outline"
                onClick={() => saveMut.mutate(!(pool?.enabled))}
                disabled={saveMut.isPending}>
                {pool?.enabled ? "disable pool" : "enable pool"}
              </Button>
            </div>
          </div>
        )}
        {err && <p className="text-sm text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}

function CacheCard({ appId, canOperate }: { appId: string; canOperate: boolean }) {
  const qc = useQueryClient();
  const [err, setErr] = useState("");
  const [prefixes, setPrefixes] = useState("");
  const [ttl, setTtl] = useState("60");

  const cache = useQuery({
    queryKey: ["cache", appId],
    queryFn: () => api.get<CachePolicy>(`/api/cache/apps/${appId}`)
      .catch(() => null),
    refetchInterval: 15_000,
  });
  const saveMut = useMutation({
    mutationFn: (enabled: boolean) => api.put(`/api/cache/apps/${appId}`, {
      enabled, ttl_s: Number(ttl) || 60,
      prefixes: prefixes.split(",").map((s) => s.trim()).filter(Boolean),
    }),
    onSuccess: () => { setErr(""); qc.invalidateQueries({ queryKey: ["cache", appId] }); },
    onError: (e) => setErr(e.message),
  });
  const purgeMut = useMutation({
    mutationFn: () => api.post(`/api/cache/apps/${appId}/purge`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cache", appId] }),
    onError: (e) => setErr(e.message),
  });

  const c = cache.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Response cache</CardTitle>
        <CardDescription>
          nx.cache — default-deny public GET/HEAD caching. Cookie and
          Authorization always bypass. Watch the X-NX-Cache response header
          for hit/miss/bypass.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {c === null && <p className="text-muted-foreground">Module disabled — enable nx.cache.</p>}
        {c?.configured && (
          <>
            <div className="flex items-center gap-2">
              <Badge variant={c.enabled ? "success" : "secondary"}>
                {c.enabled ? "on" : "off"}
              </Badge>
              <span className="font-mono text-xs text-muted-foreground">
                epoch e{c.epoch} · ttl {c.ttl_s}s · zone {c.zone_mb}m/{c.max_mb}m
              </span>
            </div>
            <div className="font-mono text-xs text-muted-foreground">
              {c.prefixes?.join(" ")}
            </div>
          </>
        )}
        {canOperate && c !== null && (
          <div className="space-y-2 border-t pt-2">
            <Input placeholder="public prefixes, comma-separated (/pub,/assets)"
              value={prefixes} onChange={(e) => setPrefixes(e.target.value)} />
            <Input placeholder="ttl seconds" value={ttl}
              onChange={(e) => setTtl(e.target.value)} />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => saveMut.mutate(true)}
                disabled={saveMut.isPending}>enable</Button>
              <Button size="sm" variant="outline"
                onClick={() => saveMut.mutate(false)}>disable</Button>
              <Button size="sm" variant="outline" onClick={() => purgeMut.mutate()}
                disabled={purgeMut.isPending || !c?.enabled}>purge</Button>
            </div>
          </div>
        )}
        {err && <p className="text-sm text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}

function OasCard({ appId, canOperate }: { appId: string; canOperate: boolean }) {
  const qc = useQueryClient();
  const [err, setErr] = useState("");
  const [specText, setSpecText] = useState("");

  const oas = useQuery({
    queryKey: ["oas", appId],
    queryFn: () => api.get<OasStatus>(`/api/openapi/apps/${appId}`)
      .catch(() => null),
    refetchInterval: 15_000,
  });
  const uploadMut = useMutation({
    mutationFn: () => api.postRaw(`/api/openapi/apps/${appId}/spec`, specText),
    onSuccess: () => { setErr(""); setSpecText(""); qc.invalidateQueries({ queryKey: ["oas", appId] }); },
    onError: (e) => setErr(e.message),
  });
  const modeMut = useMutation({
    mutationFn: (mode: string) => api.put(`/api/openapi/apps/${appId}/mode`,
      { mode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["oas", appId] }),
    onError: (e) => setErr(e.message),
  });

  const s = oas.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">OpenAPI validation</CardTitle>
        <CardDescription>
          nx.openapi — request validation against a compiled route table in
          the edge access phase. Detection or block mode; unsupported
          features are reported, never silently skipped.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {s === null && <p className="text-muted-foreground">Module disabled — enable nx.openapi.</p>}
        {s?.configured && (
          <>
            <div className="flex items-center gap-2">
              <Badge variant={s.mode === "block" ? "destructive" : "warning"}>
                {s.mode}
              </Badge>
              <Badge variant={s.applied ? "success" : "secondary"}>
                {s.applied ? "applied" : "saved only"}
              </Badge>
              <span className="font-mono text-xs text-muted-foreground">
                OAS {s.openapi_version} · {s.routes} routes · {s.spec_format}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {s.spec_name} · sha {s.spec_sha256?.slice(0, 12)}…
            </div>
            {(s.unsupported?.length ?? 0) > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-amber-500">
                  {s.unsupported?.length} unsupported feature(s) — not enforced
                </summary>
                <ul className="mt-1 space-y-0.5 font-mono text-muted-foreground">
                  {s.unsupported?.slice(0, 12).map((u, i) => (
                    <li key={i}>{u.where}: {u.feature}</li>
                  ))}
                </ul>
              </details>
            )}
            {canOperate && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  onClick={() => modeMut.mutate("detect")}
                  disabled={s.mode === "detect"}>detect mode</Button>
                <Button size="sm" variant="outline"
                  onClick={() => modeMut.mutate("block")}
                  disabled={s.mode === "block"}>block mode</Button>
              </div>
            )}
          </>
        )}
        {canOperate && s !== null && (
          <div className="space-y-2 border-t pt-2">
            <textarea
              className="h-24 w-full rounded border bg-background p-2 font-mono text-xs"
              placeholder="paste OpenAPI 3.0/3.1 JSON or YAML"
              value={specText}
              onChange={(e) => setSpecText(e.target.value)} />
            <Button size="sm" onClick={() => uploadMut.mutate()}
              disabled={uploadMut.isPending || !specText.trim()}>
              upload spec
            </Button>
          </div>
        )}
        {err && <p className="text-sm text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// M4 — nx.identity (application auth) + nx.ratelimit cards
// ---------------------------------------------------------------------------
function AuthCard({ appId, canOperate }: { appId: string; canOperate: boolean }) {
  const qc = useQueryClient();
  const [err, setErr] = useState("");
  const [providers, setProviders] = useState("");
  const [ttl, setTtl] = useState("28800");
  const [exempt, setExempt] = useState("");
  const [groups, setGroups] = useState("");

  const auth = useQuery({
    queryKey: ["appauth", appId],
    queryFn: () => api.get<AuthPolicy>(`/api/identity/apps/${appId}`)
      .catch(() => null),
    refetchInterval: 15_000,
  });
  const sessions = useQuery({
    queryKey: ["appsessions", appId],
    queryFn: () => api.get<{ sessions: AppSession[] }>(
      `/api/identity/apps/${appId}/sessions`).catch(() => null),
    enabled: !!auth.data?.enabled,
    refetchInterval: 15_000,
  });
  const saveMut = useMutation({
    mutationFn: (enabled: boolean) => api.put(`/api/identity/apps/${appId}`, {
      enabled,
      provider_ids: providers.split(",").map((s) => s.trim()).filter(Boolean),
      session_ttl_s: Number(ttl) || 28800,
      exempt_paths: exempt.split(",").map((s) => s.trim()).filter(Boolean),
      require_groups: groups.split(",").map((s) => s.trim()).filter(Boolean),
    }),
    onSuccess: () => { setErr(""); qc.invalidateQueries({ queryKey: ["appauth", appId] }); },
    onError: (e) => setErr(e.message),
  });
  const revokeMut = useMutation({
    mutationFn: (sid: number) => api.post(`/api/identity/sessions/${sid}/revoke`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["appsessions", appId] }),
    onError: (e) => setErr(e.message),
  });

  const a = auth.data;
  const live = (a?.provider_ids ?? []).join(",");
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Application authentication</CardTitle>
        <CardDescription>
          nx.identity — edge-enforced SSO for this app (OIDC/SAML/LDAP).
          Off by default; API clients get 401 JSON, never a browser redirect.
          Sessions are server-side and revocable within ~10s.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {a === null && <p className="text-muted-foreground">Module disabled — enable nx.identity.</p>}
        {a?.configured && (
          <div className="flex items-center gap-2">
            <Badge variant={a.enabled ? "success" : "secondary"}>
              {a.enabled ? "enforced" : "off"}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {a.active_sessions} session(s) · ttl {a.session_ttl_s}s
            </span>
          </div>
        )}
        {a !== null && canOperate && (
          <div className="space-y-2 border-t pt-2">
            <Input placeholder={`provider ids, comma-separated${live ? ` (now: ${live})` : ""}`}
              value={providers} onChange={(e) => setProviders(e.target.value)} />
            <Input placeholder="session ttl seconds" value={ttl}
              onChange={(e) => setTtl(e.target.value)} />
            <Input placeholder="exempt path prefixes (/health,/metrics)"
              value={exempt} onChange={(e) => setExempt(e.target.value)} />
            <Input placeholder="required groups (empty = any session)"
              value={groups} onChange={(e) => setGroups(e.target.value)} />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => saveMut.mutate(true)}
                disabled={saveMut.isPending}>enable</Button>
              <Button size="sm" variant="outline"
                onClick={() => saveMut.mutate(false)}>disable</Button>
            </div>
          </div>
        )}
        {a?.enabled && (sessions.data?.sessions?.length ?? 0) > 0 && (
          <Table>
            <THead><TR><TH>subject</TH><TH>expires</TH><TH>state</TH><TH /></TR></THead>
            <TBody>
              {sessions.data!.sessions.slice(0, 10).map((s) => (
                <TR key={s.id}>
                  <TD className="font-mono text-xs">{s.sub.slice(0, 28)}</TD>
                  <TD className="text-xs">{fmtAgo(s.expires_at)}</TD>
                  <TD>
                    <Badge variant={s.revoked || s.expired ? "destructive" : "success"}>
                      {s.revoked ? "revoked" : s.expired ? "expired" : "live"}
                    </Badge>
                  </TD>
                  <TD className="text-right">
                    {canOperate && !s.revoked && !s.expired && (
                      <Button size="sm" variant="ghost"
                        onClick={() => revokeMut.mutate(s.id)}>revoke</Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {err && <p className="text-sm text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}

function RateCard({ appId, canOperate }: { appId: string; canOperate: boolean }) {
  const qc = useQueryClient();
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [paths, setPaths] = useState("");
  const [rate, setRate] = useState("60");
  const [windowS, setWindowS] = useState("60");
  const [burst, setBurst] = useState("0");
  const [key, setKey] = useState("ip");

  const rl = useQuery({
    queryKey: ["ratelimit", appId],
    queryFn: () => api.get<RateList>(`/api/ratelimit/apps/${appId}`)
      .catch(() => null),
    refetchInterval: 15_000,
  });
  const createMut = useMutation({
    mutationFn: () => api.post(`/api/ratelimit/apps/${appId}`, {
      name: name.trim(), mode: "block",
      paths: paths.split(",").map((s) => s.trim()).filter(Boolean),
      methods: [], key, rate: Number(rate) || 1,
      window_s: Number(windowS) || 60, burst: Number(burst) || 0,
    }),
    onSuccess: () => { setErr(""); setName(""); qc.invalidateQueries({ queryKey: ["ratelimit", appId] }); },
    onError: (e) => setErr(e.message),
  });
  const delMut = useMutation({
    mutationFn: (pid: string) => api.del(`/api/ratelimit/policies/${pid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ratelimit", appId] }),
    onError: (e) => setErr(e.message),
  });

  const list = rl.data?.policies ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Rate limiting</CardTitle>
        <CardDescription>
          nx.ratelimit — per-app/path/method token counters, 429 + Retry-After.
          {rl.data && <span className="block text-xs">{rl.data.scope}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {rl.data === null && <p className="text-muted-foreground">Module disabled — enable nx.ratelimit.</p>}
        {list.length > 0 && (
          <Table>
            <THead><TR><TH>policy</TH><TH>limit</TH><TH>mode</TH><TH /></TR></THead>
            <TBody>
              {list.map((p) => (
                <TR key={p.id}>
                  <TD className="text-xs">
                    {p.name}
                    <span className="block font-mono text-[10px] text-muted-foreground">
                      {p.paths.join(",") || "all paths"} · {p.key}
                    </span>
                  </TD>
                  <TD className="font-mono text-xs">{p.rate}+{p.burst}/{p.window_s}s</TD>
                  <TD>
                    <Badge variant={p.mode === "block" ? "destructive" : "warning"}>
                      {p.expired ? "expired" : p.mode}
                    </Badge>
                  </TD>
                  <TD className="text-right">
                    {canOperate && (
                      <Button size="sm" variant="ghost"
                        onClick={() => delMut.mutate(p.id)}>delete</Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {rl.data 
!== null && canOperate && (
          <div className="space-y-2 border-t pt-2">
            <Input placeholder="policy name" value={name}
              onChange={(e) => setName(e.target.value)} />
            <Input placeholder="path regexes (^/login,^/api) — empty = all"
              value={paths} onChange={(e) => setPaths(e.target.value)} />
            <div className="flex gap-2">
              <Input placeholder="rate" value={rate}
                onChange={(e) => setRate(e.target.value)} />
              <Input placeholder="window s" value={windowS}
                onChange={(e) => setWindowS(e.target.value)} />
              <Input placeholder="burst" value={burst}
                onChange={(e) => setBurst(e.target.value)} />
              <select className="rounded border bg-background px-2 py-1 text-sm"
                value={key} onChange={(e) => setKey(e.target.value)}>
                <option value="ip">per ip</option>
                <option value="identity">per identity</option>
              </select>
            </div>
            <Button size="sm" onClick={() => createMut.mutate()}
              disabled={createMut.isPending || !name.trim()}>add policy</Button>
          </div>
        )}
        {err && <p className="text-sm text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}
